"use client";

import { useSearchLocation } from "@/hooks/use-search-location";
import { cn } from "@/lib/utils";
import { Autocomplete } from "@react-google-maps/api";
import { MapPin } from "lucide-react";
import { useState } from "react";
import { Button } from "./ui/button";

const libraries: "places"[] = ["places"];
const SUPPORTED_CITIES = new Set(["Denver"]);

interface LocationSearchProps {
  onLocationSelect?: (location: string) => void;
  onActionClick?: (location: string) => void;
  onJoinWaitlistClick?: () => void;
  initialValue?: string;
  className?: string;
  isLoading?: boolean;
}

export function LocationSearch({
  onLocationSelect,
  onActionClick,
  onJoinWaitlistClick,
  className,
  isLoading,
}: Omit<LocationSearchProps, "initialValue">) {
  const {
    isLoaded,
    onLoad,
    onPlaceChanged,
    searchValue,
    onSearchValueChange,
    isValid,
  } = useSearchLocation({
    onLocationSelect,
  });

  const handleAction = () => {
    onActionClick?.(searchValue);

    /*
    ** unused default behavior:
    if (isDenver(searchValue)) {
      window.location.href = "/login";
    } else {
      onJoinWaitlistClick?.();
    }
    */
  };

  const searchInput = () => {
    const [xPos, setXPos] = useState(0);
    const [yPos, setYPos] = useState(0);

    const handleMouseMove = (e: React.MouseEvent<HTMLButtonElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      setXPos(e.clientX - rect.left);
      setYPos(e.clientY - rect.top);
    };

    return (
      <div className="max-w-xl">
        <div className="bg-white rounded-xl shadow-xl p-2">
          <div className="flex items-center">
            <div className="flex-1 relative">
              <MapPin className="absolute left-3 top-4 text-gray-400 w-5 h-5" />
              <input
                type="text"
                className="w-full pl-10 font-semibold text-sm sm:text-lg sm:pl-12 pr-2 sm:pr-4 h-[52px] border-0 focus:ring-2 focus:ring-yellow-400 rounded-lg"
                placeholder="Where are you buying?"
                value={searchValue}
                onChange={(e) => onSearchValueChange(e.target.value)}
                // Add these attributes to prevent zoom
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
              />
            </div>
            <Button
              className={cn(
                "ml-2 bg-yellow-400 hover:bg-yellow-400 text-gray-900 px-3 sm:px-6 h-[52px] rounded-lg font-bold relative overflow-hidden group hover:shadow-lg transition-shadow",
                !isValid &&
                  "bg-gray-200 hover:bg-gray-200 text-gray-400 cursor-not-allowed hover:shadow-none animate-pulse",
              )}
              onMouseMove={handleMouseMove}
              onClick={handleAction}
              type="button"
              isLoading={isLoading}
              disabled={!isValid}
            >
              Get Started
              <span
                className="absolute inset-0 rounded-r-full bg-[radial-gradient(circle_at_var(--xPos,_0%)_var(--yPos,_0%),rgba(255,255,255,0.25)_0%,transparent_50%)] opacity-0 group-hover:opacity-100 transition-opacity"
                style={
                  {
                    "--xPos": `${xPos}px`,
                    "--yPos": `${yPos}px`,
                  } as React.CSSProperties
                }
              />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={cn("relative w-full max-w-2xl ", className)}>
      <div className="relative">
        {isLoaded ? (
          <Autocomplete onLoad={onLoad} onPlaceChanged={onPlaceChanged}>
            {searchInput()}
          </Autocomplete>
        ) : (
          searchInput()
        )}
      </div>

      {/* Status Messages */}
      {/* {locationStatus === "unsupported" && (
        <div className="mt-3 sm:absolute sm:left-14 text-center font-satoshi text-white">
          <div>Masterkey isn't available in {selectedCity} yet.</div>
        </div>
      )} */}
      <GooglePlacesDropdownStyle />
    </div>
  );
}

export const GooglePlacesDropdownStyle = () => {
  return (
    <style jsx global>{`
        .pac-container {
          z-index: 9999 !important;
          position: fixed !important;
          pointer-events: auto !important;
          background-color: white;
          border: none;
          border-radius: 0.75rem;
          margin-top: 0.5rem;
          padding: 0.5rem;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        }
        .pac-item {
          padding: 0.75rem 1rem;
          color: #1f2937;
          font-size: 1rem;
          border: none;
        }
        .pac-item:hover {
          background-color: #F3F4F6;
          cursor: pointer;
          border-radius: 0.5rem;
        }
        .pac-item-query {
          color: #1f2937;
          font-size: 1rem;
        }
        .pac-matched {
          color: #1f2937;
          font-weight: 600;
        }

        /* Hide unwanted elements */
        .pac-icon,
        .pac-container:after {
          display: none !important;
        }
        /* Keep only main text and state */
        .pac-item > span:not(.pac-item-query):not(:nth-child(3)) {
          display: none !important;
        }
        /* More specific selectors for Safari */
        div.pac-container .pac-item,
        div.pac-container .pac-item .pac-item-query {
          font-family: 'Satoshi-Regular', -apple-system, BlinkMacSystemFont, sans-serif !important;
        }

        div.pac-container .pac-item .pac-matched {
          font-family: 'Satoshi-Medium', -apple-system, BlinkMacSystemFont, sans-serif !important;
        }

        // Fix when using shadcn/ui Dialog
        [data-dialog-portal] {
          position: relative;
          z-index: auto;
        }
      `}</style>
  );
};
